import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { spApi, SP_API_BASE, SP_TOKEN_KEY, spFormatError, SP_STATUSES, labelFor } from "./api";
import { useSPAuth } from "./SPAuthContext";

const STATUS_TONE = {
  new: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  contacted: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  in_progress: "text-indigo-300 bg-indigo-500/10 border-indigo-500/30",
  qualified: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  closed: "text-violet-300 bg-violet-500/10 border-violet-500/30",
  lost: "text-rose-300 bg-rose-500/10 border-rose-500/30",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtShort = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-sm ${STATUS_TONE[status] || STATUS_TONE.new}`}>
      {labelFor(status)}
    </span>
  );
}

function MetricCard({ label, value, tone = "sky", testId }) {
  const toneCls = {
    sky: "border-sky-500/20 text-sky-300",
    amber: "border-amber-500/20 text-amber-300",
    emerald: "border-emerald-500/20 text-emerald-300",
    violet: "border-violet-500/20 text-violet-300",
    zinc: "border-zinc-800 text-zinc-100",
  }[tone];
  return (
    <div className={`border ${toneCls} bg-zinc-950/40 p-5 rounded-sm`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-2">{label}</div>
      <div className="text-3xl font-light text-zinc-50">{value}</div>
    </div>
  );
}

export default function SPLeads() {
  const { actor, formatError } = useSPAuth();
  const [metrics, setMetrics] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20, pages: 1 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);       // full lead
  const [noteDraft, setNoteDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const debounceRef = useRef();

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await spApi.get("/metrics");
      setMetrics(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await spApi.get("/leads", {
        params: { q: q.trim(), status, page, page_size: pageSize, sort: "newest" },
      });
      setData(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [q, status, page, pageSize, formatError]);

  useEffect(() => { loadMetrics(); }, [loadMetrics]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadLeads, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [loadLeads]);

  const openDetail = async (lead_id) => {
    try {
      const { data } = await spApi.get(`/leads/${lead_id}`);
      setDetail(data);
      setStatusDraft(data.status);
      setNoteDraft("");
    } catch (e) { setErr(formatError(e)); }
  };

  const closeDetail = () => {
    setDetail(null);
    setNoteDraft("");
    setStatusDraft("");
  };

  const submitUpdate = async () => {
    if (!detail) return;
    const payload = {};
    if (statusDraft && statusDraft !== detail.status) payload.status = statusDraft;
    if (noteDraft.trim()) payload.note = noteDraft.trim();
    if (!payload.status && !payload.note) return;
    setSaving(true);
    try {
      const { data } = await spApi.patch(`/leads/${detail.lead_id}`, payload);
      setDetail(data);
      setNoteDraft("");
      setStatusDraft(data.status);
      loadLeads();
      loadMetrics();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const quickSetStatus = async (lead, next) => {
    try {
      await spApi.patch(`/leads/${lead.lead_id}`, { status: next });
      loadLeads();
      loadMetrics();
    } catch (e) { setErr(formatError(e)); }
  };

  const handleExport = async () => {
    // Build URL with same filters; must attach bearer token so we use fetch
    const url = new URL(`${SP_API_BASE}/leads-export`);
    if (q.trim()) url.searchParams.set("q", q.trim());
    if (status && status !== "all") url.searchParams.set("status", status);
    const token = localStorage.getItem(SP_TOKEN_KEY);
    try {
      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `enterprise-leads-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setErr(e.message || "Export failed");
    }
  };

  const trendMax = useMemo(() => Math.max(1, ...(metrics?.trend_7d || []).map((d) => d.count)), [metrics]);

  return (
    <div className="space-y-8" data-testid="sp-leads-root">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-2">Enterprise Leads</div>
          <h1 className="text-3xl font-light text-zinc-50">All buyer conversations, one console.</h1>
          <p className="text-sm text-zinc-500 mt-2">Signed in as <span className="text-zinc-300">{actor?.email}</span></p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="text-[10px] uppercase tracking-[0.3em] border border-zinc-800 hover:border-sky-500/60 text-zinc-300 hover:text-sky-300 px-4 py-2.5 rounded-sm transition-colors"
            data-testid="sp-export-csv"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Total leads" value={metrics?.total ?? "—"} tone="zinc" testId="sp-metric-total" />
        <MetricCard label="New" value={metrics?.by_status?.new ?? "—"} tone="sky" testId="sp-metric-new" />
        <MetricCard label="Working" value={metrics?.working_count ?? "—"} tone="amber" testId="sp-metric-working" />
        <MetricCard label="Conversion" value={metrics ? `${metrics.conversion_rate}%` : "—"} tone="emerald" testId="sp-metric-conversion" />
      </div>

      {/* Trend + status breakdown */}
      {metrics ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-zinc-900 bg-zinc-950/40 p-5 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">New leads · last 7 days</div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-600">{metrics.trend_7d?.reduce((a, b) => a + b.count, 0)} total</div>
            </div>
            <div className="flex items-end gap-3 h-32">
              {metrics.trend_7d.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full bg-zinc-900 relative flex items-end" style={{ height: "100%" }}>
                    <div
                      className="w-full bg-gradient-to-t from-sky-500/70 to-sky-400/40 rounded-t-sm transition-all"
                      style={{ height: `${(d.count / trendMax) * 100}%` }}
                      title={`${d.count} leads`}
                    />
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600">{new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-zinc-900 bg-zinc-950/40 p-5 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">By status</div>
            <div className="space-y-2">
              {SP_STATUSES.map((s) => {
                const n = metrics.by_status?.[s.value] || 0;
                return (
                  <div key={s.value} className="flex items-center justify-between text-sm">
                    <StatusBadge status={s.value} />
                    <div className="text-zinc-300 tabular-nums">{n}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="border border-zinc-900 bg-zinc-950/40 rounded-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 border-b border-zinc-900">
          <input
            type="search"
            placeholder="Search by name, email, company, role…"
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            className="flex-1 bg-transparent border border-zinc-800 focus:border-sky-500 focus:outline-none px-3 py-2.5 rounded-sm text-sm placeholder-zinc-600 transition-colors"
            data-testid="sp-search-input"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setPage(1); setStatus("all"); }}
              className={`text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-sm border transition-colors ${status === "all" ? "border-sky-500 text-sky-300 bg-sky-500/10" : "border-zinc-800 text-zinc-500 hover:text-zinc-200"}`}
              data-testid="sp-filter-all"
            >
              All
            </button>
            {SP_STATUSES.map((s) => (
              <button
                key={s.value}
                onClick={() => { setPage(1); setStatus(s.value); }}
                className={`text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-sm border transition-colors ${status === s.value ? "border-sky-500 text-sky-300 bg-sky-500/10" : "border-zinc-800 text-zinc-500 hover:text-zinc-200"}`}
                data-testid={`sp-filter-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-zinc-600 border-b border-zinc-900">
                <th className="px-4 py-3 font-normal">Lead</th>
                <th className="px-4 py-3 font-normal">Company</th>
                <th className="px-4 py-3 font-normal">Size</th>
                <th className="px-4 py-3 font-normal">Received</th>
                <th className="px-4 py-3 font-normal">Status</th>
                <th className="px-4 py-3 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="sp-leads-table">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10 text-zinc-500 text-xs uppercase tracking-[0.3em]">Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-zinc-500 text-xs uppercase tracking-[0.3em]" data-testid="sp-leads-empty">No leads match your filters</td></tr>
              ) : (
                data.items.map((l) => (
                  <tr key={l.lead_id} className="border-b border-zinc-900/60 hover:bg-zinc-900/40 transition-colors cursor-pointer" onClick={() => openDetail(l.lead_id)} data-testid={`sp-lead-row-${l.lead_id}`}>
                    <td className="px-4 py-3">
                      <div className="text-zinc-100">{l.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{l.work_email}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-200">{l.company || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{l.company_size || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{fmtShort(l.created_at)}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {l.status === "new" ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); quickSetStatus(l, "contacted"); }}
                          className="text-[10px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-3 py-1.5 rounded-sm transition-colors"
                          data-testid={`sp-mark-contacted-${l.lead_id}`}
                        >
                          Mark contacted
                        </button>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">Open →</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-900 text-xs text-zinc-500">
          <div>
            Showing {data.items.length ? (data.page - 1) * data.page_size + 1 : 0}–{(data.page - 1) * data.page_size + data.items.length} of {data.total}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-zinc-800 hover:border-sky-500/60 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] uppercase tracking-[0.25em] rounded-sm transition-colors"
              data-testid="sp-page-prev"
            >
              Prev
            </button>
            <div className="px-3 tabular-nums" data-testid="sp-page-indicator">Page {data.page} / {Math.max(1, data.pages)}</div>
            <button
              disabled={page >= data.pages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-zinc-800 hover:border-sky-500/60 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] uppercase tracking-[0.25em] rounded-sm transition-colors"
              data-testid="sp-page-next"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 px-3 py-2 rounded-sm" data-testid="sp-error">{err}</div>
      ) : null}

      {/* Detail Drawer */}
      {detail ? (
        <div className="fixed inset-0 z-40" data-testid="sp-detail-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={closeDetail} />
          <aside className="absolute right-0 top-0 bottom-0 w-full sm:w-[520px] bg-[#0d1014] border-l border-zinc-900 overflow-y-auto">
            <div className="p-6 border-b border-zinc-900 flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-1">Lead</div>
                <div className="text-xl text-zinc-50">{detail.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{detail.work_email}</div>
              </div>
              <button onClick={closeDetail} className="text-zinc-500 hover:text-zinc-200 text-xs uppercase tracking-[0.25em]" data-testid="sp-drawer-close">Close</button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <Field label="Company" value={detail.company} />
                <Field label="Size" value={detail.company_size} />
                <Field label="Role" value={detail.role} />
                <Field label="Received" value={fmtDate(detail.created_at)} />
                {detail.contacted_at ? <Field label="Contacted" value={fmtDate(detail.contacted_at)} /> : null}
                {detail.contacted_by ? <Field label="Contacted by" value={detail.contacted_by} /> : null}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-2">Message</div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap bg-zinc-950/70 border border-zinc-900 p-3 rounded-sm min-h-[60px]">
                  {detail.message || <span className="text-zinc-600 italic">No message provided.</span>}
                </div>
              </div>

              <div className="border-t border-zinc-900 pt-6">
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-3">Update lead</div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {SP_STATUSES.map((s) => (
                      <button
                        key={s.value}
                        onClick={() => setStatusDraft(s.value)}
                        className={`text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border rounded-sm transition-colors ${
                          statusDraft === s.value
                            ? STATUS_TONE[s.value]
                            : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                        }`}
                        data-testid={`sp-drawer-status-${s.value}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    rows={3}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Add a note (optional)…"
                    className="w-full bg-transparent border border-zinc-800 focus:border-sky-500 focus:outline-none px-3 py-2 rounded-sm text-sm placeholder-zinc-600 transition-colors"
                    data-testid="sp-drawer-note-input"
                  />
                  <button
                    onClick={submitUpdate}
                    disabled={saving || (statusDraft === detail.status && !noteDraft.trim())}
                    className="w-full py-2.5 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-zinc-950 text-xs uppercase tracking-[0.25em] rounded-sm transition-colors"
                    data-testid="sp-drawer-save"
                  >
                    {saving ? "Saving…" : "Save update"}
                  </button>
                </div>
              </div>

              <div className="border-t border-zinc-900 pt-6">
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-3">Notes ({detail.notes?.length || 0})</div>
                {(detail.notes || []).length === 0 ? (
                  <div className="text-xs text-zinc-600 italic">No notes yet.</div>
                ) : (
                  <div className="space-y-2">
                    {[...detail.notes].reverse().map((n) => (
                      <div key={n.id} className="bg-zinc-950/70 border border-zinc-900 p-3 rounded-sm">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-1">
                          {n.actor_name || n.actor_email} · {fmtDate(n.ts)}
                        </div>
                        <div className="text-sm text-zinc-200 whitespace-pre-wrap">{n.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-zinc-900 pt-6">
                <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-3">Activity log</div>
                {(detail.activity_log || []).length === 0 ? (
                  <div className="text-xs text-zinc-600 italic">No activity yet.</div>
                ) : (
                  <ol className="relative border-l border-zinc-900 pl-4 space-y-3">
                    {[...detail.activity_log].reverse().map((a, idx) => (
                      <li key={idx} className="relative">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-sky-500/70 border border-sky-400" />
                        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                          {fmtDate(a.ts)}
                        </div>
                        <div className="text-xs text-zinc-300 mt-0.5">
                          {a.action === "status_change" ? (
                            <>Status changed from <span className="text-zinc-100">{labelFor(a.from)}</span> → <span className="text-sky-300">{labelFor(a.to)}</span></>
                          ) : a.action === "note_added" ? (
                            <>Note added</>
                          ) : (
                            a.action
                          )}
                          <span className="text-zinc-600"> · {a.actor_name || a.actor_email}</span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-1">{label}</div>
      <div className="text-zinc-200">{value || "—"}</div>
    </div>
  );
}
